import { minBy } from 'es-toolkit';
import React from 'react';
import { Group, Rect, Text } from 'react-konva';

import { Point2D } from '@paragon/contract-library';
import { Point2D as Point2DGeometry } from '@paragon/geometry-library';
import { notNullish } from '@paragon/utilities';

import { InstanceInfoValue } from './types';

interface BundlePositioningViewTrussProps {
  readonly xScale: number;
  readonly yScale: number;
  readonly instanceInfo: InstanceInfoValue;
  readonly position: Point2D;
  readonly snappingPoints?: readonly Point2DGeometry[];
  readonly onClick?: () => void;
  readonly updatePosition?: (newPosition: Point2D) => void;
  readonly selected?: boolean;
  readonly distanceFormatter: (distance: number) => string;
  readonly disabled?: boolean;
}

const textHeightAsPercentageOfThickness = 0.95;

export function BundlePositioningViewTruss(props: BundlePositioningViewTrussProps) {
  const {
    disabled,
    xScale,
    yScale,
    instanceInfo,
    position,
    onClick,
    selected,
    snappingPoints,
    updatePosition,
    distanceFormatter,
  } = props;
  const { width, height, thickness, name, plies } = instanceInfo;
  const scaledWidth = width * xScale;
  const scaledThickness = thickness * yScale;
  const snappingTolerance = (xScale + yScale) * 2;
  const scaledPosition = { x: position.x * xScale, y: position.y * yScale };
  const trussColor = disabled ? 'gray' : selected ? 'lightblue' : plies !== 1 ? 'lightgreen' : 'orange';

  return (
    <Group
      x={scaledPosition.x}
      y={scaledPosition.y}
      draggable={!!updatePosition}
      onDragStart={(element) => {
        element.target.moveToTop();
      }}
      onDragEnd={
        updatePosition && snappingPoints
          ? (event) => {
              const targetOrigin = new Point2DGeometry(event.target.x(), event.target.y());
              const targetPlusWidth = new Point2DGeometry(event.target.x() + scaledWidth, event.target.y());
              const targetPlusThickness = new Point2DGeometry(event.target.x(), event.target.y() + scaledThickness);
              const targetPlusWidthAndThickness = new Point2DGeometry(
                event.target.x() + scaledWidth,
                event.target.y() + scaledThickness,
              );
              const closestSnappingPointToTargetOrigin = minBy(snappingPoints, (snappingPosition) =>
                snappingPosition.distanceTo(targetOrigin),
              );
              const closestSnappingPointToTargetPlusWidth = minBy(snappingPoints, (snappingPosition) =>
                snappingPosition.distanceTo(targetPlusWidth),
              );
              const closestSnappingPointToTargetPlusThickness = minBy(snappingPoints, (snappingPosition) =>
                snappingPosition.distanceTo(targetPlusThickness),
              );
              const closestSnappingPointToTargetPlusWidthAndThickness = minBy(snappingPoints, (snappingPosition) =>
                snappingPosition.distanceTo(targetPlusWidthAndThickness),
              );
              const closestSnappingPointToTargetOriginDistance =
                closestSnappingPointToTargetOrigin?.distanceTo(targetOrigin);
              const closestSnappingPointToTargetPlusWidthDistance =
                closestSnappingPointToTargetPlusWidth?.distanceTo(targetPlusWidth);
              const closestSnappingPointToTargetPlusThicknessDistance =
                closestSnappingPointToTargetPlusThickness?.distanceTo(targetPlusThickness);
              const closestSnappingPointToTargetPlusWidthAndThicknessDistance =
                closestSnappingPointToTargetPlusWidthAndThickness?.distanceTo(targetPlusWidthAndThickness);
              const closestSnappingDistance = Math.min(
                ...[
                  closestSnappingPointToTargetOriginDistance,
                  closestSnappingPointToTargetPlusWidthDistance,
                  closestSnappingPointToTargetPlusThicknessDistance,
                  closestSnappingPointToTargetPlusWidthAndThicknessDistance,
                ].filter(notNullish),
              );
              if (closestSnappingDistance <= snappingTolerance) {
                if (
                  closestSnappingPointToTargetOrigin &&
                  closestSnappingPointToTargetOriginDistance === closestSnappingDistance
                ) {
                  updatePosition(closestSnappingPointToTargetOrigin);
                  return;
                }
                if (
                  closestSnappingPointToTargetPlusWidth &&
                  closestSnappingPointToTargetPlusWidthDistance === closestSnappingDistance
                ) {
                  updatePosition({
                    x: closestSnappingPointToTargetPlusWidth.x - scaledWidth,
                    y: closestSnappingPointToTargetPlusWidth.y,
                  });
                  return;
                }
                if (
                  closestSnappingPointToTargetPlusThickness &&
                  closestSnappingPointToTargetPlusThicknessDistance === closestSnappingDistance
                ) {
                  updatePosition({
                    x: closestSnappingPointToTargetPlusThickness.x,
                    y: closestSnappingPointToTargetPlusThickness.y - scaledThickness,
                  });
                  return;
                }
                if (
                  closestSnappingPointToTargetPlusWidthAndThickness &&
                  closestSnappingPointToTargetPlusWidthAndThicknessDistance === closestSnappingDistance
                ) {
                  updatePosition({
                    x: closestSnappingPointToTargetPlusWidthAndThickness.x - scaledWidth,
                    y: closestSnappingPointToTargetPlusWidthAndThickness.y - scaledThickness,
                  });
                  return;
                }
              }

              updatePosition(targetOrigin);
            }
          : undefined
      }
      onClick={onClick}
      onMouseOver={() => setCursor('pointer')}
      onMouseLeave={() => setCursor('auto')}
    >
      <Rect width={scaledWidth} height={scaledThickness} fill={trussColor} stroke="black" />
      <Text
        x={5}
        fontSize={scaledThickness * textHeightAsPercentageOfThickness}
        verticalAlign="middle"
        text={`${name} (W: ${distanceFormatter(width)}${height === undefined ? '' : ` H: ${distanceFormatter(height)}`})${
          instanceInfo.plies !== 1 ? ` ${instanceInfo.plies} plies` : ''
        }`}
      />
    </Group>
  );
}

function setCursor(cursor: string) {
  document.body.style.cursor = cursor;
}
